/**
 * Alpine.js data viewer component.
 * Renders a clean, collapsible, site-styled data tree.
 *
 * Usage: <div x-data="dataViewer({ data: ..., expanded: true })">
 */
document.addEventListener('alpine:init', () => {
    window.Alpine.data('dataViewer', ({ data, expanded = true }) => ({
        data,
        expanded,
        init() {
            this.$refs.tree.innerHTML = this.renderNode(this.data, null, 0, this.expanded);
            // Re-render when Livewire morphs the component with new data
            this.$watch('data', (newData) => {
                this.$refs.tree.innerHTML = this.renderNode(newData, null, 0, this.expanded);
            });
        },

        renderNode(value, key, depth, startOpen) {
            if (value === null || value === undefined) {
                return '<span class="dv-null text-faint italic">—</span>';
            }
            if (typeof value === 'boolean') {
                return value
                    ? '<span class="inline-flex items-center gap-1 text-success-600 dark:text-success-400 text-xs font-medium">✓ oui</span>'
                    : '<span class="inline-flex items-center gap-1 text-danger-600 dark:text-danger-400 text-xs font-medium">✗ non</span>';
            }
            if (typeof value === 'number') {
                return '<span class="dv-num text-xs font-mono font-semibold text-heading tabular-nums">' + this.formatNumber(value) + '</span>';
            }
            if (typeof value === 'string') {
                // URL
                if (/^https?:\/\/.+/.test(value)) {
                    const display = value.length > 60 ? value.substring(0, 57) + '…' : value;
                    return '<a href="' + this.esc(value) + '" target="_blank" rel="noopener" class="text-xs text-primary-600 dark:text-primary-400 hover:underline break-all">' + this.esc(display) + '</a>';
                }
                // HTML content (long with tags)
                if (value.length > 500 && /<[a-z][\s\S]*>/i.test(value)) {
                    return this.renderLongText(value, 'HTML · ' + this.wordCount(value) + ' mots');
                }
                // Long string
                if (value.length > 300) {
                    return this.renderLongText(value);
                }
                // Multi-line
                if (value.includes('\n')) {
                    return '<span class="text-xs text-body whitespace-pre-wrap leading-relaxed">' + this.esc(value) + '</span>';
                }
                return '<span class="text-xs text-body">' + this.esc(value) + '</span>';
            }

            // Array
            if (Array.isArray(value)) {
                if (value.length === 0) return '<span class="text-2xs text-faint italic">liste vide</span>';

                // Simple string/number array → inline pills
                if (value.every(v => typeof v === 'string' || typeof v === 'number') && value.length <= 20) {
                    return '<div class="flex flex-wrap gap-1 mt-0.5">'
                        + value.map(v => '<span class="inline-flex items-center px-2 py-0.5 text-2xs bg-surface-alt border border-edge-soft text-body">' + this.esc(String(v)) + '</span>').join('')
                        + '</div>';
                }

                return this.renderGroup(
                    value.length + (value.length > 1 ? ' éléments' : ' élément'),
                    value.map((item, i) => this.renderEntry(String(i), item, depth + 1, startOpen)).join(''),
                    depth, startOpen
                );
            }

            // Object
            if (typeof value === 'object') {
                const keys = Object.keys(value);
                if (keys.length === 0) return '<span class="text-2xs text-faint italic">vide</span>';

                // Single-value shortcut: {key: "simple_value"} → render inline
                if (keys.length === 1 && typeof value[keys[0]] !== 'object') {
                    return this.renderNode(value[keys[0]], keys[0], depth, startOpen);
                }

                return this.renderGroup(
                    keys.length + (keys.length > 1 ? ' propriétés' : ' propriété'),
                    keys.map(k => this.renderEntry(k, value[k], depth + 1, startOpen)).join(''),
                    depth, startOpen
                );
            }

            return '<span class="text-xs text-body">' + String(value) + '</span>';
        },

        renderEntry(key, value, depth, startOpen) {
            const label = this.humanizeKey(key);
            const isComplex = (typeof value === 'object' && value !== null);

            if (isComplex) {
                return '<div class="dv-entry">'
                    + '<div class="dv-key text-2xs font-semibold text-muted uppercase tracking-wider mb-0.5">' + this.esc(label) + '</div>'
                    + '<div class="dv-val pl-3 border-l border-edge-soft">' + this.renderNode(value, key, depth, startOpen) + '</div>'
                    + '</div>';
            }

            return '<div class="dv-entry flex items-baseline gap-2 py-0.5">'
                + '<span class="dv-key text-2xs font-medium text-muted shrink-0">' + this.esc(label) + '</span>'
                + '<span class="dv-val">' + this.renderNode(value, key, depth, startOpen) + '</span>'
                + '</div>';
        },

        renderGroup(summary, children, depth, startOpen) {
            const uid = 'dv_' + Math.random().toString(36).substr(2, 9);
            const open = startOpen && depth < 4;
            return '<div class="dv-group">'
                + '<button type="button" onclick="var e=document.getElementById(\'' + uid + '\'),a=this.querySelector(\'.dv-arrow\');if(e.style.display===\'none\'){e.style.display=\'block\';a.textContent=\'▾\'}else{e.style.display=\'none\';a.textContent=\'▸\'}" '
                + 'class="dv-toggle inline-flex items-center gap-1.5 text-2xs text-muted hover:text-body transition-colors cursor-pointer select-none">'
                + '<span class="dv-arrow">' + (open ? '▾' : '▸') + '</span>'
                + '<span>' + summary + '</span>'
                + '</button>'
                + '<div id="' + uid + '" class="dv-children space-y-1 mt-1 ml-2 pl-2 border-l border-edge-soft" style="display:' + (open ? 'block' : 'none') + '">'
                + children
                + '</div></div>';
        },

        renderLongText(text, label) {
            const uid = 'dv_' + Math.random().toString(36).substr(2, 9);
            const preview = text.replace(/<[^>]*>/g, '').substring(0, 200);
            const lbl = label || (text.length + ' car.');
            return '<div>'
                + '<span class="text-xs text-body">' + this.esc(preview) + '… </span>'
                + '<button type="button" onclick="var e=document.getElementById(\'' + uid + '\');e.style.display=e.style.display===\'none\'?\'block\':\'none\'" '
                + 'class="text-2xs text-primary-500 hover:text-primary-700 font-medium">[' + lbl + ']</button>'
                + '<div id="' + uid + '" style="display:none" class="mt-1 p-2 text-xs text-body bg-surface-alt border border-edge-soft whitespace-pre-wrap break-words max-h-60 overflow-auto">'
                + this.esc(text) + '</div></div>';
        },

        humanizeKey(key) {
            // number index → #N
            if (/^\d+$/.test(key)) return '#' + (parseInt(key) + 1);
            // snake_case → Title Case
            return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        },

        formatNumber(n) {
            if (Number.isInteger(n)) return n.toLocaleString('fr-FR');
            return n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
        },

        wordCount(text) {
            return text.replace(/<[^>]*>/g, '').trim().split(/\s+/).filter(Boolean).length;
        },

        esc(str) {
            return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        }
    }));
});
